import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  createFilterOptions,
} from '@mui/material';
import { Clock, History, RotateCw, Send } from 'lucide-react';
import api from '../../services/api';
import { useGetEmployeesQuery } from '../../store/slices/employeeSlice';
import type { Employee } from '../../store/slices/employeeSlice';
import { getEmployeeSummaryRecipients } from '../../utils/employeeSummaryRecipients';
import { PageHeader, SectionCard, SpecGrid, Tag, useDelayedFlag } from '../../components/ui';
import { focusRing, sbg } from '../../theme/sbgTokens';
import {
  MISSING,
  formatCount,
  formatDate,
  formatDateTime,
  formatNumber,
  formatRelative,
  formatTime,
} from '../../utils/format';

/* ------------------------------------------------------------------------ */
/* Programs                                                                  */
/* ------------------------------------------------------------------------ */

interface Recipient {
  name: string;
  /** An address or qualifier, shown on its own line so it never breaks mid-word. */
  detail?: string;
}

interface EmailAction {
  id: string;
  title: string;
  /** Short name for outcomes: "VP report not sent". */
  shortName: string;
  /** Who would get a second copy if it went out twice. */
  audience: string;
  description: string;
  recipients: Recipient[];
  /** When the backend sends it on its own (jobs/dailyDigestJob, jobs/leadDistributionJob). */
  schedule: string;
  /** Verb + object for the page and confirm buttons. */
  sendLabel: string;
  endpoint: string;
  confirmTitle: string;
  confirmMessage: string;
  resultKey?: string;
}

const emailActions: EmailAction[] = [
  {
    id: 'vp-summary',
    title: 'VP daily OOO report',
    shortName: 'VP report',
    audience: 'VPs',
    description: 'Today’s company-wide absences, with out, WFH and sick counts and each person’s department and hours.',
    recipients: [{ name: 'Every VP', detail: 'From employee VP assignments' }],
    schedule: 'Weekdays at 9:00 AM ET',
    sendLabel: 'Send VP report',
    endpoint: '/admin/emails/vp-daily-summary',
    confirmTitle: 'Send today’s VP OOO report now?',
    confirmMessage: 'Every VP receives today’s OOO summary now, in addition to the automatic 9:00 AM ET send.',
  },
  {
    id: 'coverage-alerts',
    title: 'Manager coverage alerts',
    shortName: 'coverage alerts',
    audience: 'managers',
    description: 'Warns a department’s manager when 25% or more of the team is out today, with the PTO, sick and WFH breakdown.',
    recipients: [{ name: 'Department managers', detail: 'Only where 25% or more of the team is out' }],
    schedule: 'Weekdays at 9:00 AM ET',
    sendLabel: 'Send coverage alerts',
    endpoint: '/admin/emails/coverage-alerts',
    confirmTitle: 'Send coverage alerts now?',
    confirmMessage:
      'Managers whose team is at least 25% out today receive a coverage alert now, in addition to the automatic 9:00 AM ET send.',
  },
  {
    id: 'lead-distribution',
    title: 'Lead distribution report',
    shortName: 'lead report',
    audience: 'T. Flynn and Emma',
    description:
      'Whose leads to pause today (all-day absences, WFH without VP lead approval) and whose to keep on, with partial-day times.',
    recipients: [
      { name: 'T. Flynn', detail: 'tflynn@sbgfunding.com' },
      { name: 'Emma', detail: 'emma@sbgfunding.com' },
    ],
    schedule: 'Weekdays at 8:30 AM ET',
    sendLabel: 'Send lead report',
    endpoint: '/admin/emails/lead-distribution',
    confirmTitle: 'Send today’s lead distribution report now?',
    confirmMessage:
      'T. Flynn and Emma receive today’s lead distribution report now, in addition to the automatic 8:30 AM ET send.',
  },
];

/* ------------------------------------------------------------------------ */
/* Send state                                                                */
/* ------------------------------------------------------------------------ */

type SummarySendOptions = {
  batchPercent?: number;
  batchNumber?: number;
  sendAll?: boolean;
};

/** One monthly-summary send, described in the words the page and dialog use. */
interface SummaryScope {
  /** Duplicate-guard key: employee:<id>, batch:<percent>:<number> or all. */
  key: string;
  kind: 'employee' | 'batch' | 'all';
  employeeId?: string;
  options?: SummarySendOptions;
  /** Where the result shows on the page; also the in-flight key. */
  resultKey: string;
  /** Standalone name: "Batch 2 of 5", "Maria Chen’s summary", "Every summary". */
  label: string;
  /** In a sentence: "batch 2", "Maria Chen’s summary", "every summary". */
  noun: string;
  /** Who a repeat send reaches again: "the 43 people in it", "Maria Chen", "everyone". */
  people: string;
  /** Summaries this send targets. */
  count: number;
  batchNumber?: number;
  batchCount?: number;
  /** What happens when it's confirmed. */
  message: string;
}

type SendOutcome = 'sent' | 'partial' | 'unconfirmed';

/** What this tab knows about an earlier send of one scope. */
interface SummarySendRecord {
  outcome: SendOutcome;
  /** Epoch ms. Missing on entries saved by the earlier, string-valued version of this page. */
  at?: number;
  label?: string;
  sent?: number;
  targeted?: number;
  /** Plain cause for an unconfirmed send. */
  cause?: string;
}

/** Why a send didn't confirm, in words a person can act on. */
interface SendFailure {
  /** ambiguous: some email may have gone out. definite: the server says it didn't. */
  certainty: 'ambiguous' | 'definite';
  cause: string;
  nextStep: string;
}

type PendingEmailSend = { kind: 'action'; action: EmailAction } | { kind: 'summary'; scope: SummaryScope };

type SendResultSeverity = 'success' | 'warning' | 'error';

interface DashboardSendResult {
  severity: SendResultSeverity;
  message: string;
  /** When the outcome came back (epoch ms), shown in ET beside the message. */
  at: number;
}

const SEND_SLOW_WARNING_MS = 15_000;
/** Kept from the version that stored plain warning strings, so a recorded risk survives the upgrade. */
const SUMMARY_SEND_LOG_STORAGE_KEY = 'attendance:summary-duplicate-risks';
const BATCH_PERCENT_OPTIONS = [10, 15, 20, 25, 33, 50];
const SEND_OUTCOMES: SendOutcome[] = ['sent', 'partial', 'unconfirmed'];

/*
 * Layout follows the card's own width, not the viewport: the sidebar takes
 * 240px from 768px up (less when collapsed), so viewport breakpoints split
 * these panels far too early.
 */
const SUMMARY_CONTAINER = 'email-summary';
const SUMMARY_SPLIT = `@container ${SUMMARY_CONTAINER} (min-width: 680px)`;
const SUMMARY_ROW = `@container ${SUMMARY_CONTAINER} (min-width: 520px)`;
const PROGRAMS_CONTAINER = 'email-programs';
const PROGRAMS_WIDE = `@container ${PROGRAMS_CONTAINER} (min-width: 600px)`;

const getSummaryScopeKey = (employeeId?: string, options?: SummarySendOptions) => {
  if (employeeId) return `employee:${employeeId}`;
  if (!options?.sendAll && options?.batchPercent !== undefined) {
    return `batch:${options.batchPercent}:${options.batchNumber || 1}`;
  }
  return 'all';
};

const readSendLog = (): Record<string, SummarySendRecord> => {
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(SUMMARY_SEND_LOG_STORAGE_KEY) || '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const log: Record<string, SummarySendRecord> = {};
    Object.entries(parsed as Record<string, unknown>).forEach(([scopeKey, value]) => {
      if (typeof value === 'string') {
        // Saved by the earlier version: a duplicate-risk warning with no detail.
        log[scopeKey] = { outcome: 'unconfirmed' };
      } else if (value && typeof value === 'object' && SEND_OUTCOMES.includes((value as SummarySendRecord).outcome)) {
        log[scopeKey] = value as SummarySendRecord;
      }
    });
    return log;
  } catch {
    return {};
  }
};

const isInactive = (employee?: Employee | null) =>
  Boolean(employee?.employmentStatus && employee.employmentStatus !== 'active');

const capFirst = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

const isToday = (at: number) => formatDate(at) === formatDate(Date.now());

/** "at 9:03 AM ET" today, "on Oct 7, 2026, 9:03 AM ET" otherwise. */
const whenPhrase = (at?: number) => (!at ? 'earlier' : isToday(at) ? `at ${formatTime(at)}` : `on ${formatDateTime(at)}`);

/** "9:03 AM ET" today, "Oct 7" otherwise: for menus and captions. */
const whenShort = (at: number) => (isToday(at) ? formatTime(at) : formatDate(at, { short: true }));

const OUTCOME_SHORT: Record<SendOutcome, string> = { sent: 'sent', partial: 'partly sent', unconfirmed: 'unconfirmed' };

/** "sent 9:03 AM ET": the note beside a batch in the Batch menu. */
const recordShort = (record: SummarySendRecord) =>
  `${OUTCOME_SHORT[record.outcome]}${record.at ? ` ${whenShort(record.at)}` : ''}`;

/** One sentence under a send control: what already happened to this scope. */
const recordSentence = (record: SummarySendRecord, kind: SummaryScope['kind']) => {
  const when = whenPhrase(record.at);
  if (record.outcome === 'sent') return `Already sent ${when}.`;
  if (record.outcome === 'partial') {
    return record.sent !== undefined && record.targeted
      ? `Partly sent ${when}: ${formatNumber(record.sent)} of ${formatCount(record.targeted, 'summary', 'summaries')} went out.`
      : `Partly sent ${when}.`;
  }
  return kind === 'employee' ? `May have sent ${when}; the server didn’t confirm it.` : `May have partly sent ${when}; the server didn’t confirm it.`;
};

/** The warning in the confirm dialog when this exact scope went out before. */
const priorSendCopy = (record: SummarySendRecord, scope: SummaryScope) => {
  const when = whenPhrase(record.at);
  if (record.outcome === 'sent') {
    return { title: `You sent ${scope.noun} ${when}`, body: `Sending again emails ${scope.people} a second time.` };
  }
  if (record.outcome === 'partial') {
    const reached =
      record.sent !== undefined && record.targeted
        ? `${formatNumber(record.sent)} of ${formatCount(record.targeted, 'summary', 'summaries')} went out. `
        : 'Some summaries went out. ';
    return {
      title: `${capFirst(scope.noun)} partly sent ${when}`,
      body: `${reached}Sending again emails those people a second time.`,
    };
  }
  return {
    title: `${capFirst(scope.noun)} may have ${scope.kind === 'employee' ? '' : 'partly '}sent ${when}`,
    body:
      `${record.cause ?? 'The server didn’t confirm the send.'} ` +
      `Sending again can email ${scope.kind === 'employee' ? scope.people : 'some employees'} twice.`,
  };
};

/** "Send everyone" after batches or single sends from this tab reaches those people again. */
const overlapCopy = (records: SummarySendRecord[]) => {
  const latest = records.slice().sort((a, b) => (b.at ?? 0) - (a.at ?? 0))[0];
  const latestText = latest.label
    ? `${latest.label}, ${OUTCOME_SHORT[latest.outcome]} ${whenPhrase(latest.at)}`
    : 'an earlier send';
  const more = records.length > 1 ? `, and ${formatCount(records.length - 1, 'other send')}` : '';
  return {
    title: 'Some people already have theirs',
    body: `Earlier from this tab: ${latestText}${more}. Sending to everyone emails those people a second time.`,
  };
};

/**
 * Turns a failed request into a plain cause and next step. A gateway timeout,
 * no response, or a server-reported unconfirmed delivery (504) is ambiguous:
 * some email may already be out. Anything else the server answered is definite.
 */
const readSendFailure = (error: any, target: PendingEmailSend): SendFailure => {
  if (error?.sendFailure) return error.sendFailure as SendFailure;
  const status: number | undefined = error?.response?.status;
  const isSummary = target.kind === 'summary';
  const waitStep = 'The email may still arrive, so wait a few minutes before you send again.';

  if (!error?.response || status === 504) {
    const data = error?.response?.data?.data;
    const unconfirmed = Number(data?.deliveryUnknown || 0);
    const targeted = Number(data?.targeted || 0);
    let cause: string;
    if (unconfirmed > 0 && targeted > 0) {
      cause =
        `The email provider confirmed ${formatNumber(Number(data?.sent || 0))} of ` +
        `${formatCount(targeted, 'summary', 'summaries')} and couldn’t confirm the other ${formatNumber(unconfirmed)}.`;
    } else if (status === 504 || error?.code === 'ECONNABORTED') {
      cause = 'The send timed out before the server confirmed it.';
    } else if (error?.isAxiosError) {
      cause = 'The server didn’t respond.';
    } else {
      cause = 'The page didn’t get a reply from the server.';
    }
    return { certainty: 'ambiguous', cause, nextStep: waitStep };
  }

  const retryStep = 'Try again in a few minutes.';
  if (status === 401 || status === 403) {
    return { certainty: 'definite', cause: 'Your account can’t send this email.', nextStep: 'Sign in with an admin or HR account, then try again.' };
  }
  if (target.kind === 'summary' && target.scope.kind === 'employee' && status === 404) {
    return {
      certainty: 'definite',
      cause: `${capFirst(target.scope.people)} isn’t on the roster or has no email address on file.`,
      nextStep: 'Add an email on the employee’s record, then send again.',
    };
  }
  if (status === 422) {
    return {
      certainty: 'definite',
      cause: 'No one in this send can receive a summary.',
      nextStep: 'Check that these employees are active and have an email address on file.',
    };
  }
  if (status === 502) {
    return {
      certainty: 'definite',
      cause: isSummary ? 'The email provider didn’t accept the summaries.' : 'The email provider didn’t accept it.',
      nextStep: retryStep,
    };
  }
  return {
    certainty: 'definite',
    cause: isSummary ? 'The server couldn’t prepare the summaries.' : 'The server couldn’t send it.',
    nextStep: retryStep,
  };
};

/** The outcome as a title: "Batch 2 may have partly sent", "VP report not sent". */
const failureTitle = (target: PendingEmailSend, failure: SendFailure) => {
  const ambiguous = failure.certainty === 'ambiguous';
  if (target.kind === 'action') {
    return ambiguous ? `Couldn’t confirm the ${target.action.shortName}` : `${capFirst(target.action.shortName)} not sent`;
  }
  const { scope } = target;
  if (scope.kind === 'employee') return ambiguous ? `${capFirst(scope.noun)} may have sent` : 'Summary not sent';
  if (scope.kind === 'batch') {
    return ambiguous ? `Batch ${scope.batchNumber} may have partly sent` : `Batch ${scope.batchNumber} not sent`;
  }
  return ambiguous ? 'Summaries may have partly sent' : 'Summaries not sent';
};

/** What a repeat could do, for an ambiguous failure. */
const failureConsequence = (target: PendingEmailSend) => {
  if (target.kind === 'action') return `Sending it again could email ${target.action.audience} twice.`;
  const { scope } = target;
  if (scope.kind === 'employee') return `Sending again can email ${scope.people} twice.`;
  if (scope.kind === 'batch') return `Sending batch ${scope.batchNumber} again can email some employees twice.`;
  return 'Sending again can email some employees twice.';
};

const employeeFilter = createFilterOptions<Employee>({
  stringify: (employee) => `${employee.name || ''} ${employee.email || ''}`,
});

/* ------------------------------------------------------------------------ */
/* Presentational pieces                                                     */
/* ------------------------------------------------------------------------ */

/** The outcome of the last send for one program, with the time it came back. */
const ResultAlert: React.FC<{ result?: DashboardSendResult }> = ({ result }) => {
  if (!result) return null;
  return (
    <Alert severity={result.severity} role={result.severity === 'error' ? 'alert' : 'status'}>
      {result.message}
      <Box component="span" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
        {' · '}
        {formatTime(result.at)}
      </Box>
    </Alert>
  );
};

/** A quiet line under a send control: what already went out from this tab. */
const PriorSendNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="body2"
    component="p"
    sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}
  >
    <Box component="span" aria-hidden="true" sx={{ display: 'flex', flex: 'none', mt: '2px' }}>
      <History size={14} />
    </Box>
    <span>{children}</span>
  </Typography>
);

/** "Roster updated 5m ago", with the exact time in a tooltip; re-reads the clock every minute. */
const RosterFreshness: React.FC<{ at: number }> = ({ at }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  const relative = formatRelative(at, new Date(now));
  // "Just now" / "Yesterday" read mid-sentence; a date ("Oct 7, 2026") keeps its capital.
  const phrase = /^[A-Z][a-z]{2} \d/.test(relative) ? relative : relative.charAt(0).toLowerCase() + relative.slice(1);
  return (
    <Tooltip title={formatDateTime(at)} describeChild>
      <Box
        component="time"
        dateTime={new Date(at).toISOString()}
        tabIndex={0}
        sx={{ borderRadius: sbg.radius.xs, '&:focus-visible': focusRing }}
      >
        Roster updated {phrase}
      </Box>
    </Tooltip>
  );
};

/** A subtle grouping inside the summary card (book §11.8, Subtle card). */
const SubPanel: React.FC<{ title: string; description: string; headingId: string; children: React.ReactNode }> = ({
  title,
  description,
  headingId,
  children,
}) => (
  <Box
    component="section"
    aria-labelledby={headingId}
    sx={{
      bgcolor: sbg.bg.surfaceSubtle,
      borderRadius: sbg.radius.lg,
      p: 2.5,
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      minWidth: 0,
    }}
  >
    <Box>
      <Typography id={headingId} variant="labelMd" component="h3" sx={{ color: sbg.text.primary, display: 'block' }}>
        {title}
      </Typography>
      <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: 0.25 }}>
        {description}
      </Typography>
    </Box>
    {children}
  </Box>
);

/** One automatic program: a ledger row in the Daily reports card. */
const DailyReportRow: React.FC<{
  action: EmailAction;
  result?: DashboardSendResult;
  isSending: boolean;
  onSend: () => void;
}> = ({ action, result, isSending, onSend }) => (
  <Box
    component="li"
    sx={{
      px: 2.5,
      py: 2.5,
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr)',
      columnGap: 3,
      rowGap: 2,
      '&:not(:first-of-type)': { borderTop: `1px solid ${sbg.border.subtle}` },
      [PROGRAMS_WIDE]: { gridTemplateColumns: 'minmax(0, 1fr) auto' },
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="labelMd" component="h3" sx={{ color: sbg.text.primary, display: 'block' }}>
        {action.title}
      </Typography>
      <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: 0.25 }}>
        {action.description}
      </Typography>

      <Box
        component="dl"
        sx={{
          m: 0,
          mt: 1.5,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          columnGap: 3,
          rowGap: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography component="dt" variant="labelSm" sx={{ color: sbg.text.secondary, display: 'block' }}>
            Schedule
          </Typography>
          <Box
            component="dd"
            sx={{ m: 0, mt: 0.25, display: 'flex', alignItems: 'center', gap: 0.75, fontVariantNumeric: 'tabular-nums' }}
          >
            <Box component="span" aria-hidden="true" sx={{ display: 'flex', color: sbg.text.secondary }}>
              <Clock size={14} />
            </Box>
            <Typography variant="body1" component="span" sx={{ color: sbg.text.primary }}>
              {action.schedule}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="dt" variant="labelSm" sx={{ color: sbg.text.secondary, display: 'block' }}>
            Goes to
          </Typography>
          <Box component="dd" sx={{ m: 0, mt: 0.25 }}>
            <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gap: 0.75 }}>
              {action.recipients.map((recipient) => (
                <li key={recipient.name}>
                  <Typography variant="body1" component="span" sx={{ display: 'block', color: sbg.text.primary }}>
                    {recipient.name}
                  </Typography>
                  {recipient.detail && (
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{ display: 'block', color: sbg.text.secondary, overflowWrap: 'break-word' }}
                    >
                      {recipient.detail}
                    </Typography>
                  )}
                </li>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>

    <Box sx={{ [PROGRAMS_WIDE]: { gridColumn: 2, gridRow: 1, alignSelf: 'start' } }}>
      <Button
        variant="outlined"
        onClick={onSend}
        disabled={isSending}
        aria-busy={isSending || undefined}
        startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : <Send />}
        sx={{ width: { xs: '100%', sm: 'auto' } }}
      >
        {isSending ? 'Sending…' : action.sendLabel}
      </Button>
    </Box>

    {result && (
      <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>
        <ResultAlert result={result} />
      </Box>
    )}
  </Box>
);

/* ------------------------------------------------------------------------ */
/* Page                                                                      */
/* ------------------------------------------------------------------------ */

const EmailDashboard: React.FC = () => {
  const [sending, setSending] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, DashboardSendResult>>({});
  const [pendingSend, setPendingSend] = useState<PendingEmailSend | null>(null);
  const [pendingSendFailure, setPendingSendFailure] = useState<SendFailure | null>(null);
  const [sendIsSlow, setSendIsSlow] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [summaryBatchPercent, setSummaryBatchPercent] = useState<number>(20);
  const [summaryBatchNumber, setSummaryBatchNumber] = useState<number>(1);
  // Per-tab record of summary sends (sessionStorage): what went out, what may
  // have, and when. Drives the duplicate warnings; never sent to the server.
  const [summarySendLog, setSummarySendLog] = useState<Record<string, SummarySendRecord>>(readSendLog);
  const {
    data: employees = [],
    isLoading: employeesLoading,
    isError: employeesError,
    refetch: refetchEmployees,
    fulfilledTimeStamp: employeesFetchedAt,
  } = useGetEmployeesQuery();
  const summaryRecipients = useMemo(
    () => getEmployeeSummaryRecipients(employees),
    [employees]
  );
  const employeeOptions = useMemo(
    () => employees.slice().sort((a, b) => a.name.localeCompare(b.name)),
    [employees]
  );
  const selectedEmployeeRecord = employeeOptions.find((employee) => employee._id === selectedEmployee) ?? null;

  const batchPlan = useMemo(() => {
    const total = summaryRecipients.length;
    const percent = Math.max(1, Math.min(100, Math.floor(summaryBatchPercent || 0) || 20));
    const batchSize = total > 0 ? Math.max(1, Math.ceil((total * percent) / 100)) : 0;
    const batchCount = batchSize > 0 ? Math.ceil(total / batchSize) : 1;
    const safeBatchNumber = Math.max(1, Math.min(summaryBatchNumber, batchCount));
    const start = batchSize > 0 ? (safeBatchNumber - 1) * batchSize + 1 : 0;
    const end = batchSize > 0 ? Math.min(start + batchSize - 1, total) : 0;
    const targeted = batchSize > 0 ? Math.max(0, end - start + 1) : 0;
    // summaryRecipients is sorted to match the server's slice, so these are
    // the first and last people the selected batch reaches.
    const firstName = targeted > 0 ? summaryRecipients[start - 1]?.name : undefined;
    const lastName = targeted > 0 ? summaryRecipients[end - 1]?.name : undefined;

    return { total, percent, batchSize, batchCount, safeBatchNumber, start, end, targeted, firstName, lastName };
  }, [summaryRecipients, summaryBatchPercent, summaryBatchNumber]);

  useEffect(() => {
    if (summaryBatchNumber !== batchPlan.safeBatchNumber) {
      setSummaryBatchNumber(batchPlan.safeBatchNumber);
    }
  }, [batchPlan.safeBatchNumber, summaryBatchNumber]);

  useEffect(() => {
    if (!sending) {
      setSendIsSlow(false);
      return;
    }

    const timeoutId = window.setTimeout(() => setSendIsSlow(true), SEND_SLOW_WARNING_MS);
    return () => window.clearTimeout(timeoutId);
  }, [sending]);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(SUMMARY_SEND_LOG_STORAGE_KEY, JSON.stringify(summarySendLog));
    } catch {
      // Storage can be unavailable in hardened browser sessions. The in-memory
      // guard still protects the current page session.
    }
  }, [summarySendLog]);

  const clearResult = (key: string) => {
    setResults((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const recordFailure = (key: string, target: PendingEmailSend, failure: SendFailure) => {
    const severity: SendResultSeverity = failure.certainty === 'ambiguous' ? 'warning' : 'error';
    setResults((prev) => ({
      ...prev,
      [key]: { severity, message: `${failureTitle(target, failure)}. ${failure.cause}`, at: Date.now() },
    }));
    setPendingSendFailure(failure);
  };

  const requestActionSend = (action: EmailAction) => {
    setPendingSendFailure(null);
    setPendingSend({ kind: 'action', action });
  };

  const handleSend = async (action: EmailAction) => {
    setSending(action.id);
    setPendingSendFailure(null);
    clearResult(action.id);
    try {
      const response = await api.post(action.endpoint);
      const data = response.data?.data;
      let message = response.data?.message || 'Emails sent';
      if (action.resultKey && data?.[action.resultKey] !== undefined) {
        message = `${data[action.resultKey]} emails sent`;
        if (data.skipped) message += ` (${data.skipped} skipped)`;
        if (data.errors) message += ` (${data.errors} errors)`;
      }
      setResults((prev) => ({ ...prev, [action.id]: { severity: 'success', message, at: Date.now() } }));
      setPendingSend(null);
    } catch (error: any) {
      const target: PendingEmailSend = { kind: 'action', action };
      recordFailure(action.id, target, readSendFailure(error, target));
    } finally {
      setSending(null);
    }
  };

  const buildSummaryScope = (employeeId?: string, options?: SummarySendOptions): SummaryScope => {
    const key = getSummaryScopeKey(employeeId, options);
    if (employeeId) {
      const employee = employees.find((candidate) => candidate._id === employeeId);
      const name = employee?.name || 'this employee';
      const message = employee?.email
        ? `${capFirst(name)} receives their monthly summary at ${employee.email}.` +
          (isInactive(employee) ? ' They’re inactive, so batches skip them, but this send still goes out.' : '')
        : `${capFirst(name)} has no email address on file, so this send will fail. Add one on their employee record first.`;
      return {
        key,
        kind: 'employee',
        employeeId,
        resultKey: `summary-${employeeId}`,
        label: `${capFirst(name)}’s summary`,
        noun: `${name}’s summary`,
        people: name,
        count: 1,
        message,
      };
    }

    if (!options?.sendAll && options?.batchPercent !== undefined) {
      const batchNumber = options.batchNumber || 1;
      const range =
        batchPlan.start === batchPlan.end ? `employee ${batchPlan.start}` : `employees ${batchPlan.start}–${batchPlan.end}`;
      const names =
        batchPlan.firstName && batchPlan.lastName
          ? batchPlan.targeted === 1
            ? `: ${batchPlan.firstName}`
            : `, ${batchPlan.firstName} through ${batchPlan.lastName}`
          : '';
      return {
        key,
        kind: 'batch',
        options,
        resultKey: 'summary-batch',
        label: `Batch ${batchNumber} of ${batchPlan.batchCount}`,
        noun: `batch ${batchNumber}`,
        people: `the ${formatCount(batchPlan.targeted, 'person', 'people')} in it`,
        count: batchPlan.targeted,
        batchNumber,
        batchCount: batchPlan.batchCount,
        message:
          `Batch ${batchNumber} of ${batchPlan.batchCount} sends ` +
          `${formatCount(batchPlan.targeted, 'summary', 'summaries')} to ${range} of ${batchPlan.total}${names}.`,
      };
    }

    return {
      key,
      kind: 'all',
      options,
      resultKey: 'summary-all',
      label: 'Every summary',
      noun: 'every summary',
      people: 'everyone',
      count: summaryRecipients.length,
      message:
        'Every active employee with an email on file receives their summary now: ' +
        `${formatCount(summaryRecipients.length, 'summary', 'summaries')} in one request.`,
    };
  };

  const requestSummarySend = (employeeId?: string, options?: SummarySendOptions) => {
    setPendingSendFailure(null);
    setPendingSend({ kind: 'summary', scope: buildSummaryScope(employeeId, options) });
  };

  const handleSendSummary = async (scope: SummaryScope) => {
    const key = scope.resultKey;
    const target: PendingEmailSend = { kind: 'summary', scope };

    setSending(key);
    setPendingSendFailure(null);
    clearResult(key);
    try {
      const payload =
        scope.kind === 'employee'
          ? { employeeId: scope.employeeId }
          : scope.kind === 'batch'
            ? { batchPercent: scope.options?.batchPercent, batchNumber: scope.options?.batchNumber }
            : {};
      const response = await api.post('/admin/emails/employee-summaries', payload);
      const data = response.data?.data;
      if (scope.kind === 'employee' && data?.sent !== 1) {
        const sendFailure: SendFailure = data?.errors
          ? { certainty: 'definite', cause: 'The email provider didn’t accept the summary.', nextStep: 'Try again in a few minutes.' }
          : {
              certainty: 'definite',
              cause: `${capFirst(scope.people)} has no email address on file, so nothing was sent.`,
              nextStep: 'Add an email on the employee’s record, then send again.',
            };
        throw Object.assign(new Error('Summary not confirmed'), { sendFailure });
      }

      const sent = Number(data?.sent || 0);
      const targeted = Number(data?.targeted || 0);
      const failed = Number(data?.errors || 0);
      const skipped = Number(data?.skipped || 0);
      const isPartial = scope.kind !== 'employee' && (failed > 0 || skipped > 0 || sent < targeted);
      const tail = [failed ? `${formatNumber(failed)} failed` : null, skipped ? `${formatNumber(skipped)} skipped` : null]
        .filter(Boolean)
        .join(', ');
      const batchNumber = Number(data?.batch?.batchNumber) || scope.batchNumber || 1;
      const batchCount = Number(data?.batch?.batchCount) || scope.batchCount || batchPlan.batchCount;

      let message: string;
      if (scope.kind === 'employee') {
        message = `Summary sent to ${scope.people}`;
      } else if (scope.kind === 'batch') {
        message = isPartial
          ? `Batch ${batchNumber} of ${batchCount} partly sent: ${formatNumber(sent)} of ${formatCount(targeted, 'summary', 'summaries')}`
          : `Batch ${batchNumber} of ${batchCount} sent: ${formatCount(sent, 'summary', 'summaries')}`;
      } else {
        message = isPartial
          ? `Partly sent: ${formatNumber(sent)} of ${formatCount(targeted, 'summary', 'summaries')}`
          : `${formatCount(sent, 'summary', 'summaries')} sent`;
      }
      if (tail) message += `, ${tail}`;

      const at = Date.now();
      setResults((prev) => ({ ...prev, [key]: { severity: isPartial ? 'warning' : 'success', message, at } }));
      setSummarySendLog((current) => ({
        ...current,
        [scope.key]: {
          outcome: isPartial ? 'partial' : 'sent',
          at,
          label: scope.label,
          sent: scope.kind === 'employee' ? 1 : sent,
          targeted: scope.kind === 'employee' ? 1 : targeted,
        },
      }));
      // A confirmed batch moves the page on to the next one, so the obvious
      // next click is the next batch, not a second copy of this one.
      if (scope.kind === 'batch' && !isPartial && batchNumber < batchCount) {
        setSummaryBatchNumber(batchNumber + 1);
      }
      setPendingSend(null);
    } catch (error: any) {
      const failure = readSendFailure(error, target);
      if (failure.certainty === 'ambiguous') {
        setSummarySendLog((current) => ({
          ...current,
          [scope.key]: { outcome: 'unconfirmed', at: Date.now(), label: scope.label, cause: failure.cause },
        }));
      }
      recordFailure(key, target, failure);
    } finally {
      setSending(null);
    }
  };

  const handleConfirmSend = () => {
    if (!pendingSend || sending) return;
    if (pendingSend.kind === 'action') {
      void handleSend(pendingSend.action);
      return;
    }
    void handleSendSummary(pendingSend.scope);
  };

  const closeSendDialog = () => {
    if (sending) return;
    setPendingSend(null);
    setPendingSendFailure(null);
  };

  const isSendingSummary = Boolean(sending?.startsWith('summary'));

  /* ---- Roster state (drives the summary figures) ---------------------- */
  const rosterUnavailable = employeesError && employees.length === 0;
  const rosterLoading = employeesLoading && employees.length === 0;
  const showRosterSkeleton = useDelayedFlag(rosterLoading);
  const rosterReady = !rosterLoading && !rosterUnavailable;
  const rosterFigure = (value: React.ReactNode) =>
    rosterLoading ? (
      <Skeleton variant="rounded" width={112} height={28} sx={{ visibility: showRosterSkeleton ? 'visible' : 'hidden' }} />
    ) : rosterUnavailable ? (
      MISSING
    ) : (
      value
    );

  const singleSendKey = `summary-${selectedEmployee}`;
  const singleSending = isSendingSummary && sending === singleSendKey;
  const batchSending = isSendingSummary && sending === 'summary-batch';
  const allSending = isSendingSummary && sending === 'summary-all';

  /* ---- What already went out from this tab ---------------------------- */
  const batchKey = (num: number) => getSummaryScopeKey(undefined, { batchPercent: batchPlan.percent, batchNumber: num });
  const batchRecord = batchPlan.total > 0 ? summarySendLog[batchKey(batchPlan.safeBatchNumber)] : undefined;
  const allBatchesSent =
    batchPlan.total > 0 &&
    Array.from({ length: batchPlan.batchCount }, (_, i) => i + 1).every((num) => summarySendLog[batchKey(num)]?.outcome === 'sent');
  const singleRecord = selectedEmployee ? summarySendLog[getSummaryScopeKey(selectedEmployee)] : undefined;
  const allRecord = summarySendLog.all;
  const latestRecord = Object.values(summarySendLog)
    .filter((record): record is SummarySendRecord & { at: number } => typeof record.at === 'number')
    .sort((a, b) => b.at - a.at)[0];

  const latestCaption = !latestRecord
    ? 'Nothing sent from this tab yet'
    : latestRecord.outcome === 'sent'
      ? `Sent ${whenShort(latestRecord.at)}${latestRecord.sent ? ` · ${formatCount(latestRecord.sent, 'summary', 'summaries')}` : ''}`
      : latestRecord.outcome === 'partial'
        ? `Partly sent ${whenShort(latestRecord.at)}` +
          (latestRecord.sent !== undefined && latestRecord.targeted
            ? ` · ${formatNumber(latestRecord.sent)} of ${formatCount(latestRecord.targeted, 'summary', 'summaries')}`
            : '')
        : `Not confirmed · ${whenShort(latestRecord.at)}`;

  /* ---- Confirm dialog -------------------------------------------------- */
  const pendingScope = pendingSend?.kind === 'summary' ? pendingSend.scope : undefined;
  const priorRecord = pendingScope ? summarySendLog[pendingScope.key] : undefined;
  const overlapRecords =
    pendingScope?.kind === 'all' && !priorRecord
      ? Object.entries(summarySendLog).filter(([scopeKey]) => scopeKey !== 'all').map(([, record]) => record)
      : [];
  const duplicateWarning =
    pendingScope && priorRecord
      ? priorSendCopy(priorRecord, pendingScope)
      : overlapRecords.length > 0
        ? overlapCopy(overlapRecords)
        : null;

  const dialogTitle = !pendingSend
    ? ''
    : pendingSendFailure
      ? failureTitle(pendingSend, pendingSendFailure)
      : pendingSend.kind === 'action'
        ? pendingSend.action.confirmTitle
        : priorRecord
          ? `Send ${pendingSend.scope.noun} again?`
          : pendingSend.scope.kind === 'employee'
            ? `Send a summary to ${pendingSend.scope.people}?`
            : pendingSend.scope.kind === 'batch'
              ? `Send summary batch ${pendingSend.scope.batchNumber}?`
              : 'Send every summary now?';
  const dialogMessage = !pendingSend
    ? ''
    : pendingSend.kind === 'action'
      ? pendingSend.action.confirmMessage
      : pendingSend.scope.message;
  const confirmLabel = !pendingSend
    ? 'Send email'
    : pendingSend.kind === 'action'
      ? pendingSend.action.sendLabel
      : (pendingSend.scope.kind === 'employee'
          ? 'Send summary'
          : pendingSend.scope.kind === 'all'
            ? `Send ${formatCount(pendingSend.scope.count, 'summary', 'summaries')}`
            : `Send batch ${pendingSend.scope.batchNumber}`) + (priorRecord ? ' again' : '');

  /* ---- Copy that depends on roster state ------------------------------- */
  const batchHelper = rosterLoading ? (
    'Loading the employee roster…'
  ) : rosterUnavailable ? (
    'Batches appear once the roster loads.'
  ) : batchPlan.total === 0 ? (
    'Batches become available once an active employee has an email on file.'
  ) : (
    <>
      Batch {batchPlan.safeBatchNumber}:{' '}
      <Box component="span" sx={{ color: sbg.text.primary }}>
        {batchPlan.targeted === 1 ? batchPlan.firstName : `${batchPlan.firstName} through ${batchPlan.lastName}`}
      </Box>{' '}
      ({formatCount(batchPlan.targeted, 'employee')},{' '}
      {batchPlan.start === batchPlan.end ? batchPlan.start : `${batchPlan.start}–${batchPlan.end}`} of{' '}
      {formatNumber(batchPlan.total)}).
    </>
  );
  const allCaption = rosterLoading
    ? 'Loading the employee roster…'
    : rosterUnavailable
      ? 'Available once the roster loads.'
      : summaryRecipients.length === 0
        ? 'Available once an active employee has an email on file.'
        : `All ${formatCount(summaryRecipients.length, 'summary', 'summaries')} go out in one request. Use batches unless everything has to go now.`;
  const batchButtonLabel =
    batchPlan.total === 0
      ? 'Send batch'
      : `Send batch ${batchPlan.safeBatchNumber} of ${batchPlan.batchCount}${batchRecord ? ' again' : ''}`;
  const allButtonLabel =
    summaryRecipients.length === 0
      ? 'Send all summaries'
      : `Send all ${formatNumber(summaryRecipients.length)} summaries${allRecord ? ' again' : ''}`;
  const singleHelper = !selectedEmployeeRecord
    ? 'Choose an employee to send their summary.'
    : !selectedEmployeeRecord.email
      ? 'No email on file. This send will fail until one is added.'
      : isInactive(selectedEmployeeRecord)
        ? `Inactive. Batches skip them, but this send still goes to ${selectedEmployeeRecord.email}.`
        : selectedEmployeeRecord.email;

  return (
    <Box>
      <PageHeader
        overline="Insights"
        title="Email activity"
        meta={[
          'Email to employees, managers and VPs',
          'Daily reports on weekdays, 8:30–9:00 AM ET',
          employeesFetchedAt ? <RosterFreshness at={employeesFetchedAt} /> : null,
        ]}
      />

      <Box sx={{ display: 'grid', gap: 3 }}>
        {/* Monthly employee summary ------------------------------------- */}
        <SectionCard
          id="monthly-summary"
          title="Monthly employee summary"
          description="Each employee’s PTO and sick usage, balances, pacing and scheduled time off, sent to them personally. Send it once a month."
          action={<Tag>Manual</Tag>}
          bodySx={{ display: 'grid', gap: 3, containerType: 'inline-size', containerName: SUMMARY_CONTAINER }}
        >
          {rosterUnavailable && (
            <Alert
              severity="error"
              action={
                <Button size="small" variant="text" color="inherit" startIcon={<RotateCw />} onClick={() => refetchEmployees()}>
                  Try again
                </Button>
              }
            >
              <AlertTitle>Couldn’t load the employee roster</AlertTitle>
              Recipients and batches appear once it loads. Nothing has been sent.
            </Alert>
          )}
          {rosterReady && batchPlan.total === 0 && (
            <Alert
              severity="info"
              action={
                <Button size="small" variant="text" color="inherit" component={RouterLink} to="/employees">
                  Open employees
                </Button>
              }
            >
              <AlertTitle>No employees can receive a summary yet</AlertTitle>
              Summaries go to active employees with an email address on file.
            </Alert>
          )}

          <SpecGrid
            minColumnWidth={180}
            items={[
              {
                label: 'Recipients',
                value: rosterFigure(formatCount(batchPlan.total, 'employee')),
                caption: 'Active, with an email on file',
              },
              {
                label: 'Last sent from this tab',
                value: latestRecord ? latestRecord.label ?? 'An earlier send' : MISSING,
                caption: latestCaption,
              },
            ]}
          />

          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: 'minmax(0, 1fr)',
              alignItems: 'start',
              [SUMMARY_SPLIT]: { gridTemplateColumns: 'minmax(0, 7fr) minmax(0, 5fr)' },
            }}
          >
            <SubPanel
              headingId="summary-batches-title"
              title="Send in batches"
              description="Recommended. Each batch goes out on its own, so you can check results before sending the next."
            >
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
                <TextField
                  select
                  label="Batch size"
                  value={summaryBatchPercent}
                  onChange={(e) => setSummaryBatchPercent(Number(e.target.value))}
                  slotProps={{ select: { sx: { fontVariantNumeric: 'tabular-nums' } } }}
                >
                  {BATCH_PERCENT_OPTIONS.map((pct) => (
                    <MenuItem key={pct} value={pct} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {pct}% of recipients
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Batch"
                  value={batchPlan.safeBatchNumber}
                  onChange={(e) => setSummaryBatchNumber(Number(e.target.value))}
                  disabled={batchPlan.total === 0}
                  slotProps={{
                    select: {
                      sx: { fontVariantNumeric: 'tabular-nums' },
                      renderValue: (value: unknown) =>
                        batchPlan.total === 0 ? MISSING : `${value as number} of ${batchPlan.batchCount}`,
                    },
                  }}
                >
                  {Array.from({ length: batchPlan.batchCount }, (_, i) => i + 1).map((num) => {
                    const record = batchPlan.total > 0 ? summarySendLog[batchKey(num)] : undefined;
                    return (
                      <MenuItem key={num} value={num} sx={{ fontVariantNumeric: 'tabular-nums', gap: 1.5 }}>
                        <span>
                          {num} of {batchPlan.batchCount}
                        </span>
                        {record && (
                          <Box component="span" sx={{ ml: 'auto', color: sbg.text.secondary }}>
                            {recordShort(record)}
                          </Box>
                        )}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Box>

              <Box sx={{ display: 'grid', gap: 0.75 }}>
                <Typography variant="body1" component="p" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
                  {batchHelper}
                </Typography>
                {allBatchesSent ? (
                  <PriorSendNote>All {formatCount(batchPlan.batchCount, 'batch', 'batches')} sent from this tab.</PriorSendNote>
                ) : (
                  batchRecord && <PriorSendNote>{recordSentence(batchRecord, 'batch')}</PriorSendNote>
                )}
              </Box>

              <ResultAlert result={results['summary-batch']} />

              <Box>
                <Button
                  variant="contained"
                  disabled={isSendingSummary || batchPlan.total === 0}
                  onClick={() => requestSummarySend(undefined, { batchPercent: batchPlan.percent, batchNumber: batchPlan.safeBatchNumber })}
                  aria-busy={batchSending || undefined}
                  startIcon={batchSending ? <CircularProgress size={16} color="inherit" /> : <Send />}
                  sx={{ fontVariantNumeric: 'tabular-nums', width: { xs: '100%', sm: 'auto' } }}
                >
                  {batchSending ? 'Sending batch…' : batchButtonLabel}
                </Button>
              </Box>
            </SubPanel>

            <SubPanel
              headingId="summary-single-title"
              title="Send to one employee"
              description="For a new hire, a corrected balance, or anyone a batch missed."
            >
              <Autocomplete
                options={employeeOptions}
                value={selectedEmployeeRecord}
                onChange={(_, next) => setSelectedEmployee(next?._id ?? '')}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                filterOptions={employeeFilter}
                loading={rosterLoading}
                loadingText="Loading employees…"
                noOptionsText={rosterUnavailable ? 'The roster didn’t load' : 'No employees match'}
                renderOption={(props, option) => {
                  const { key: _key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & { key?: React.Key };
                  const secondary = [isInactive(option) ? 'Inactive' : null, option.email || 'No email on file']
                    .filter(Boolean)
                    .join(' · ');
                  return (
                    <Box component="li" key={option._id} {...optionProps} sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
                      <Typography variant="body1" component="span" noWrap sx={{ color: sbg.text.primary, flex: 'none', maxWidth: '55%' }}>
                        {option.name}
                      </Typography>
                      <Typography variant="body2" component="span" noWrap sx={{ color: sbg.text.secondary, minWidth: 0 }}>
                        {secondary}
                      </Typography>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Employee"
                    placeholder="e.g. Maria Chen or maria@sbgfunding.com"
                    helperText={singleHelper}
                  />
                )}
              />

              {singleRecord && <PriorSendNote>{recordSentence(singleRecord, 'employee')}</PriorSendNote>}

              <ResultAlert result={results[singleSendKey]} />

              <Box>
                <Button
                  variant="outlined"
                  disabled={!selectedEmployee || isSendingSummary}
                  onClick={() => requestSummarySend(selectedEmployee)}
                  aria-busy={singleSending || undefined}
                  startIcon={singleSending ? <CircularProgress size={16} color="inherit" /> : <Send />}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  {singleSending ? 'Sending…' : singleRecord ? 'Send summary again' : 'Send summary'}
                </Button>
              </Box>
            </SubPanel>
          </Box>

          <Box
            component="section"
            aria-labelledby="summary-all-title"
            sx={{
              borderTop: `1px solid ${sbg.border.subtle}`,
              pt: 2.5,
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: 'minmax(0, 1fr)',
              alignItems: 'center',
              [SUMMARY_ROW]: { gridTemplateColumns: 'minmax(0, 1fr) auto' },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography id="summary-all-title" variant="labelMd" component="h3" sx={{ color: sbg.text.primary, display: 'block' }}>
                Send to everyone at once
              </Typography>
              <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: 0.25, fontVariantNumeric: 'tabular-nums' }}>
                {allCaption}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              disabled={isSendingSummary || summaryRecipients.length === 0}
              onClick={() => requestSummarySend(undefined, { sendAll: true })}
              aria-busy={allSending || undefined}
              startIcon={allSending ? <CircularProgress size={16} color="inherit" /> : <Send />}
              sx={{ fontVariantNumeric: 'tabular-nums', justifySelf: 'stretch', [SUMMARY_ROW]: { justifySelf: 'end' } }}
            >
              {allSending ? 'Sending…' : allButtonLabel}
            </Button>
            {allRecord && (
              <Box sx={{ gridColumn: '1 / -1' }}>
                <PriorSendNote>{recordSentence(allRecord, 'all')}</PriorSendNote>
              </Box>
            )}
            {results['summary-all'] && (
              <Box sx={{ gridColumn: '1 / -1' }}>
                <ResultAlert result={results['summary-all']} />
              </Box>
            )}
          </Box>
        </SectionCard>

        {/* Daily reports ------------------------------------------------- */}
        <SectionCard
          id="daily-reports"
          title="Daily reports"
          description="These send automatically every weekday. Send one now only when today’s needs to go out again."
          action={<Tag>Automatic</Tag>}
          flush
        >
          <Box
            component="ul"
            sx={{ m: 0, p: 0, listStyle: 'none', containerType: 'inline-size', containerName: PROGRAMS_CONTAINER }}
          >
            {emailActions.map((action) => (
              <DailyReportRow
                key={action.id}
                action={action}
                result={results[action.id]}
                isSending={sending === action.id}
                onSend={() => requestActionSend(action)}
              />
            ))}
          </Box>
        </SectionCard>
      </Box>

      <Dialog
        open={Boolean(pendingSend)}
        onClose={closeSendDialog}
        disableEscapeKeyDown={Boolean(sending)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="email-send-dialog-title"
        aria-describedby="email-send-dialog-message"
      >
        <DialogTitle id="email-send-dialog-title">{dialogTitle}</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2 }}>
          {pendingSend && pendingSendFailure ? (
            // After a failed send the title states the outcome; one alert says
            // why and what to do. Sending again starts from the page.
            <Alert
              severity={pendingSendFailure.certainty === 'ambiguous' ? 'warning' : 'error'}
              role="alert"
              id="email-send-dialog-message"
            >
              {pendingSendFailure.cause}{' '}
              {pendingSendFailure.certainty === 'ambiguous' && `${failureConsequence(pendingSend)} `}
              {pendingSendFailure.nextStep}
            </Alert>
          ) : (
            <>
              <Typography
                id="email-send-dialog-message"
                variant="body1"
                sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}
              >
                {dialogMessage}
              </Typography>
              {duplicateWarning && (
                <Alert severity="warning">
                  <AlertTitle>{duplicateWarning.title}</AlertTitle>
                  {duplicateWarning.body}
                </Alert>
              )}
              {sending && (
                <Alert severity="info" role="status" icon={<CircularProgress size={16} />}>
                  {sendIsSlow
                    ? 'Still sending. Keep this dialog open: the request is still running, and retrying an email whose status is unknown could send it twice.'
                    : 'Sending now. This usually takes a few seconds.'}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={closeSendDialog} disabled={Boolean(sending)}>
            {pendingSendFailure ? 'Close' : 'Cancel'}
          </Button>
          {!pendingSendFailure && (
            <Button
              variant="contained"
              color={duplicateWarning ? 'error' : 'primary'}
              onClick={handleConfirmSend}
              disabled={Boolean(sending)}
              aria-busy={Boolean(sending) || undefined}
              startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <Send />}
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {sending ? 'Sending…' : confirmLabel}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailDashboard;
